import { useEffect, useRef, useState, type ReactNode } from "react";
import { Alert } from "./Alert";
import { useConfig } from "../state/config-context";

interface StepUpDialogProps {
  open: boolean;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (code: string) => void;
}

export function StepUpDialog({
  open,
  busy,
  error,
  onCancel,
  onSubmit,
}: StepUpDialogProps): ReactNode {
  const { t } = useConfig();
  const [code, setCode] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setCode("");
      inputRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && !busy) {
        onCancel();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="modal-backdrop"
      onClick={() => {
        if (!busy) {
          onCancel();
        }
      }}
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="stepup-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="stepup-title">{t("apiKeys.stepUpTitle")}</h2>
        <p>{t("apiKeys.stepUpHint")}</p>
        {error !== null ? <Alert tone="error">{error}</Alert> : null}
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (code.length === 6) {
              onSubmit(code);
            }
          }}
        >
          <label className="field" htmlFor="stepup-code">
            <span>{t("mfa.code")}</span>
            <input
              id="stepup-code"
              ref={inputRef}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
              required
            />
          </label>
          <div className="form-actions">
            <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn btn--primary" disabled={busy || code.length !== 6}>
              {busy ? t("mfa.submitting") : t("apiKeys.stepUpSubmit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
